import React, { useState, useEffect } from 'react';
import { useRazorpay } from 'react-razorpay';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tag, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Payment = () => {
  const [Razorpay] = useRazorpay();
  const location = useLocation();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Promo discount from location state (if redirected from promo redemption)
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);

  useEffect(() => {
    // Check if redirected from promo redemption with discount
    if (location.state) {
      const { plan, originalAmount, discountedAmount, discountPercentage, promoCode } = location.state;
      if (plan && discountedAmount) {
        setPlanDetails(plan);
        setAmount(Math.round(discountedAmount).toString());
        setPromoDiscount({
          code: promoCode,
          percentage: discountPercentage,
          originalAmount: originalAmount,
          discountedAmount: discountedAmount
        });
      }
    }
  }, [location]);

  const handlePayment = async () => {
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create order
      const orderResponse = await axios.post(
        `${API}/api/payments/create-order`,
        {
          amount: parseInt(amount),
          currency: 'INR'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { order_id, amount: orderAmount, currency, key_id } = orderResponse.data;

      // Razorpay options
      const options = {
        key: key_id,
        amount: orderAmount,
        currency: currency,
        name: 'Nerd AI',
        description: 'Payment',
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await axios.post(
              `${API}/api/payments/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (verifyResponse.data.success) {
              toast.success('Payment successful!');
              setAmount('');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: localStorage.getItem('userName') || '',
          email: localStorage.getItem('userEmail') || ''
        },
        theme: {
          color: '#ff6b35'
        }
      };

      const razorpayInstance = new Razorpay(options);
      
      razorpayInstance.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        toast.error('Payment failed. Please try again.');
      });

      razorpayInstance.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            {planDetails ? planDetails.name : 'Make a Payment'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Promo Discount Banner */}
          {promoDiscount && (
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="h-5 w-5 text-green-600" />
                <span className="font-bold text-green-800">
                  Promo Code Applied: {promoDiscount.code}
                </span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Original Amount:</span>
                  <span className="text-gray-900 line-through">₹{promoDiscount.originalAmount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700 font-semibold">Discount ({promoDiscount.percentage}%):</span>
                  <span className="text-green-700 font-semibold">
                    -₹{Math.round(promoDiscount.originalAmount - promoDiscount.discountedAmount)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-green-300 pt-2 mt-2">
                  <span className="font-bold text-green-800">Final Amount:</span>
                  <span className="font-bold text-green-800 text-lg">₹{Math.round(promoDiscount.discountedAmount)}</span>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-green-700 text-xs">
                <CheckCircle className="h-3 w-3" />
                <span>You're saving ₹{Math.round(promoDiscount.originalAmount - promoDiscount.discountedAmount)}!</span>
              </div>
            </div>
          )}

          {/* Plan Details */}
          {planDetails && !promoDiscount && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-900 mb-2">{planDetails.name}</h3>
              <div className="text-2xl font-bold text-blue-600">₹{planDetails.amount}</div>
              {planDetails.credits && (
                <div className="text-sm text-blue-700 mt-1">{planDetails.credits} Credits</div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">
              Amount (₹)
            </label>
            <Input
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading || promoDiscount}
              min="1"
            />
            {promoDiscount && (
              <p className="text-xs text-green-600 mt-1">
                Amount locked with promo discount
              </p>
            )}
          </div>

          <Button
            onClick={handlePayment}
            disabled={loading || !amount}
            className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900"
          >
            {loading ? 'Processing...' : `Pay ₹${amount || '0'}`}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Secure payment powered by Razorpay
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Payment;
